import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { db } from "../firebase";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { quizSections } from "./QuizWeights";

// Define section names exactly as they appear in the quiz
const SECTION_NAMES = {
  DIET: "Diet",
  EXERCISE: "Exercise",
  SLEEP: "Sleep",
  COMMUNITY: "Community",
  MENTAL_ACTIVITY: "MentalActivity", // no space in Firebase
  STRESS_MANAGEMENT: "StressManagement", // no space in Firebase
  GENERAL_HEALTH: "GeneralHealth", // no space in Firebase
  MENTAL_HEALTH: "MentalHealth", // no space in Firebase
  ENVIRONMENTAL_FACTORS: "EnvironmentalFactors", // no space in Firebase
};

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
  "#FF6B6B",
  "#6B66FF",
  "#FFD700",
];

// Required authentication key
const AUTH_KEY = "bfusa-uhfhk-jp04m";

const NeuroAgeAdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);

  // Check authentication on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authKey = urlParams.get('auth');
    
    if (authKey === AUTH_KEY) {
      setAuthenticated(true);
    } else {
      setAuthenticated(false);
      setLoading(false);
    }
  }, []);

  // Calculate question score based on quiz weights
  const calculateQuestionScore = (questionId, answer) => {
    // Find the question in quizSections
    for (const section of quizSections) {
      const question = section.questions.find(q => q.id === questionId);
      if (question) {
        const option = question.options.find(opt => opt[0] === answer);
        if (option) {
          return option[1]; // Return the score value
        }
      }
    }
    return 0;
  };

  // Fetch data from Firebase when component mounts and is authenticated
  useEffect(() => {
    const fetchQuizScores = async () => {
      if (!authenticated) return;
      
      try {
        setLoading(true);
        setError(null);

        const scoresQuery = query(
          collection(db, "quiz-scores"),
          orderBy("timestamp", "desc")
        );

        const querySnapshot = await getDocs(scoresQuery);

        if (querySnapshot.empty) {
          setError(
            "No quiz data found. Please complete a quiz to see results."
          );
          setLoading(false);
          return;
        }

        const scores = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          const sectionScores = data.sectionScores || {};

          // Process section scores to match the expected format
          return {
            id: doc.id,
            name: data.name || "Anonymous",
            email: data.email || "No email",
            completedDate: data.timestamp
              ? new Date(data.timestamp.toDate()).toISOString().split("T")[0]
              : "N/A",
            overall: data.overall || 0,
            // Map section scores using the exact keys from Firebase
            diet: sectionScores[SECTION_NAMES.DIET] || 0,
            exercise: sectionScores[SECTION_NAMES.EXERCISE] || 0,
            sleep: sectionScores[SECTION_NAMES.SLEEP] || 0,
            community: sectionScores[SECTION_NAMES.COMMUNITY] || 0,
            mentalActivity: sectionScores[SECTION_NAMES.MENTAL_ACTIVITY] || 0,
            stress: sectionScores[SECTION_NAMES.STRESS_MANAGEMENT] || 0,
            generalHealth: sectionScores[SECTION_NAMES.GENERAL_HEALTH] || 0,
            mentalHealth: sectionScores[SECTION_NAMES.MENTAL_HEALTH] || 0,
            environment:
              sectionScores[SECTION_NAMES.ENVIRONMENTAL_FACTORS] || 0,
            // Store raw answers for CSV export
            rawAnswers: data.answers || {},
            timestamp: data.timestamp,
          };
        });

        setUserData(scores);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching quiz scores:", err);
        setError("Failed to load quiz data. Please try again later.");
        setLoading(false);
      }
    };

    fetchQuizScores();
  }, [authenticated]);

  // Function to export data as CSV
  const exportToCSV = () => {
    if (userData.length === 0) {
      alert("No data to export");
      return;
    }

    // Create comprehensive column headers
    const headers = [
      // Basic info
      "ID", "Name", "Email", "Completed Date", 
      // Overall and pillar scores
      "Overall Score",
      "Diet Score", "Exercise Score", "Sleep Score", "Community Score",
      "Mental Activity Score", "Stress Management Score", "General Health Score",
      "Mental Health Score", "Environmental Factors Score"
    ];

    // Add headers for each question (answer and score)
    quizSections.forEach(section => {
      section.questions.forEach(question => {
        headers.push(`${question.id}_answer`);
        headers.push(`${question.id}_score`);
      });
    });

    // Create CSV rows
    const rows = userData.map(user => {
      const baseData = [
        user.id,
        user.name,
        user.email,
        user.completedDate,
        user.overall.toFixed(2),
        user.diet.toFixed(2),
        user.exercise.toFixed(2),
        user.sleep.toFixed(2),
        user.community.toFixed(2),
        user.mentalActivity.toFixed(2),
        user.stress.toFixed(2),
        user.generalHealth.toFixed(2),
        user.mentalHealth.toFixed(2),
        user.environment.toFixed(2),
      ];

      // Add answer and score for each question
      const questionData = [];
      quizSections.forEach(section => {
        section.questions.forEach(question => {
          const answer = user.rawAnswers[question.id];
          const score = answer !== undefined ? calculateQuestionScore(question.id, answer) : "";
          
          // Add both answer and score
          questionData.push(answer !== undefined ? String(answer).replace(/,/g, ';') : "");
          questionData.push(score !== "" ? score.toFixed(1) : "");
        });
      });

      return [...baseData, ...questionData];
    });

    // Convert to CSV format
    const csvContent = [
      headers.join(","),
      ...rows.map(row => 
        row.map(cell => {
          // Escape cells containing commas or quotes
          const cellStr = String(cell);
          if (cellStr.includes(",") || cellStr.includes('"') || cellStr.includes("\n")) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(",")
      )
    ].join("\n");

    // Create and download the file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    
    link.setAttribute("href", url);
    link.setAttribute("download", `neuroage_quiz_data_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Calculate correlation between two arrays
  const calculateCorrelation = (arr1, arr2) => {
    if (arr1.length !== arr2.length || arr1.length === 0) return 0;
    
    const n = arr1.length;
    const sum1 = arr1.reduce((a, b) => a + b, 0);
    const sum2 = arr2.reduce((a, b) => a + b, 0);
    const sum1Sq = arr1.reduce((a, b) => a + b * b, 0);
    const sum2Sq = arr2.reduce((a, b) => a + b * b, 0);
    const pSum = arr1.reduce((a, b, i) => a + b * arr2[i], 0);
    
    const num = pSum - (sum1 * sum2 / n);
    const den = Math.sqrt((sum1Sq - sum1 * sum1 / n) * (sum2Sq - sum2 * sum2 / n));
    
    if (den === 0) return 0;
    return num / den;
  };

  // Get mood correlations
  const getMoodCorrelations = () => {
    const usersWithMood = userData.filter(u => 
      u.rawAnswers?.mental_health_overallmood !== undefined
    );
    
    if (usersWithMood.length < 3) return null; // Need at least 3 data points
    
    const moods = usersWithMood.map(u => 
      parseFloat(u.rawAnswers.mental_health_overallmood) || 0
    );
    
    const correlations = [
      {
        pillar: "Overall Score",
        correlation: calculateCorrelation(moods, usersWithMood.map(u => u.overall)),
        color: "#8884d8"
      },
      {
        pillar: "Diet",
        correlation: calculateCorrelation(moods, usersWithMood.map(u => u.diet)),
        color: "#82ca9d"
      },
      {
        pillar: "Exercise",
        correlation: calculateCorrelation(moods, usersWithMood.map(u => u.exercise)),
        color: "#ffc658"
      },
      {
        pillar: "Sleep",
        correlation: calculateCorrelation(moods, usersWithMood.map(u => u.sleep)),
        color: "#ff8042"
      },
      {
        pillar: "Community",
        correlation: calculateCorrelation(moods, usersWithMood.map(u => u.community)),
        color: "#00C49F"
      },
      {
        pillar: "Mental Activity",
        correlation: calculateCorrelation(moods, usersWithMood.map(u => u.mentalActivity)),
        color: "#FFBB28"
      },
      {
        pillar: "Stress Management",
        correlation: calculateCorrelation(moods, usersWithMood.map(u => u.stress)),
        color: "#FF6B6B"
      },
      {
        pillar: "General Health",
        correlation: calculateCorrelation(moods, usersWithMood.map(u => u.generalHealth)),
        color: "#6B66FF"
      },
      {
        pillar: "Mental Health",
        correlation: calculateCorrelation(moods, usersWithMood.map(u => u.mentalHealth)),
        color: "#FFD700"
      }
    ].sort((a, b) => Math.abs(b.correlation) - Math.abs(a.correlation));
    
    return { correlations, sampleSize: usersWithMood.length };
  };

  // Get sharpness correlations
  const getSharpnessCorrelations = () => {
    const usersWithSharpness = userData.filter(u => 
      u.rawAnswers?.mental_sharpness !== undefined
    );
    
    if (usersWithSharpness.length < 3) return null; // Need at least 3 data points
    
    const sharpness = usersWithSharpness.map(u => 
      parseFloat(u.rawAnswers.mental_sharpness) || 0
    );
    
    const correlations = [
      {
        pillar: "Overall Score",
        correlation: calculateCorrelation(sharpness, usersWithSharpness.map(u => u.overall)),
        color: "#8884d8"
      },
      {
        pillar: "Diet",
        correlation: calculateCorrelation(sharpness, usersWithSharpness.map(u => u.diet)),
        color: "#82ca9d"
      },
      {
        pillar: "Exercise",
        correlation: calculateCorrelation(sharpness, usersWithSharpness.map(u => u.exercise)),
        color: "#ffc658"
      },
      {
        pillar: "Sleep",
        correlation: calculateCorrelation(sharpness, usersWithSharpness.map(u => u.sleep)),
        color: "#ff8042"
      },
      {
        pillar: "Community",
        correlation: calculateCorrelation(sharpness, usersWithSharpness.map(u => u.community)),
        color: "#00C49F"
      },
      {
        pillar: "Mental Activity",
        correlation: calculateCorrelation(sharpness, usersWithSharpness.map(u => u.mentalActivity)),
        color: "#FFBB28"
      },
      {
        pillar: "Stress Management",
        correlation: calculateCorrelation(sharpness, usersWithSharpness.map(u => u.stress)),
        color: "#FF6B6B"
      },
      {
        pillar: "General Health",
        correlation: calculateCorrelation(sharpness, usersWithSharpness.map(u => u.generalHealth)),
        color: "#6B66FF"
      },
      {
        pillar: "Mental Health",
        correlation: calculateCorrelation(sharpness, usersWithSharpness.map(u => u.mentalHealth)),
        color: "#FFD700"
      }
    ].sort((a, b) => Math.abs(b.correlation) - Math.abs(a.correlation));
    
    return { correlations, sampleSize: usersWithSharpness.length };
  };

  // If not authenticated, show access denied message
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-4">
            You need proper authentication to access this dashboard.
          </p>
          <p className="text-sm text-gray-500">
            Please contact the administrator for access.
          </p>
        </div>
      </div>
    );
  }

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setActiveTab("userDetail");
  };

  // Function to aggregate data for dashboard metrics
  const getDashboardMetrics = () => {
    if (loading || userData.length === 0) {
      return {
        monthlyData: [],
        weaknessAreas: [],
      };
    }

    // Monthly signups - count completions by month
    const monthlyMap = {};
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    userData.forEach((user) => {
      if (user.completedDate !== "N/A") {
        const month = new Date(user.completedDate).getMonth();
        const monthName = months[month];
        monthlyMap[monthName] = (monthlyMap[monthName] || 0) + 1;
      }
    });

    const monthlyData = Object.keys(monthlyMap).map((month) => ({
      month,
      signups: monthlyMap[month],
    }));

    // Identify weakness areas - using human-readable labels for display
    const weaknessAreas = [
      { name: "Diet", count: userData.filter((u) => u.diet < 6).length },
      {
        name: "Exercise",
        count: userData.filter((u) => u.exercise < 6).length,
      },
      { name: "Sleep", count: userData.filter((u) => u.sleep < 6).length },
      {
        name: "Community",
        count: userData.filter((u) => u.community < 6).length,
      },
      {
        name: "Mental Activity",
        count: userData.filter((u) => u.mentalActivity < 6).length,
      },
      {
        name: "Stress Management",
        count: userData.filter((u) => u.stress < 6).length,
      },
      {
        name: "General Health",
        count: userData.filter((u) => u.generalHealth < 6).length,
      },
      {
        name: "Mental Health",
        count: userData.filter((u) => u.mentalHealth < 6).length,
      },
      {
        name: "Environmental Factors",
        count: userData.filter((u) => u.environment < 6).length,
      },
    ];

    return {
      monthlyData,
      weaknessAreas,
    };
  };

  const renderOverview = () => {
    const { monthlyData, weaknessAreas } = getDashboardMetrics();
    const moodData = getMoodCorrelations();
    const sharpnessData = getSharpnessCorrelations();

    if (loading) {
      return <div className="text-center py-10">Loading dashboard data...</div>;
    }

    if (error && userData.length === 0) {
      return (
        <div className="bg-white p-6 rounded-lg shadow-md text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <p className="text-gray-600">
            Complete a quiz to see data visualizations here.
          </p>
        </div>
      );
    }

    if (userData.length === 0) {
      return (
        <div className="bg-white p-6 rounded-lg shadow-md text-center">
          <p className="text-gray-600 mb-4">No quiz data available yet.</p>
          <p className="text-gray-600">
            Complete a quiz to see data visualizations here.
          </p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-xl font-semibold mb-4">
            Monthly Test Completions
          </h3>
          {monthlyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="signups" fill="#4C9F70" name="Completions" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center py-10 text-gray-500">
              No monthly data available yet
            </p>
          )}
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-xl font-semibold mb-4">Common Weakness Areas</h3>
          {weaknessAreas.some((area) => area.count > 0) ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={weaknessAreas.filter((area) => area.count > 0)}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="count"
                  nameKey="name"
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                >
                  {weaknessAreas.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center py-10 text-gray-500">
              No weakness data available yet
            </p>
          )}
        </div>

        {/* Mood Correlation Analysis */}
        <div className="bg-white p-6 rounded-lg shadow-md md:col-span-2">
          <h3 className="text-xl font-semibold mb-4">Mood Correlations with Health Pillars</h3>
          {moodData ? (
            <>
              <div className="text-center text-sm text-gray-600 mb-4">
                Based on {moodData.sampleSize} users | Correlation strength: 
                <span className="text-red-600"> ±0.8-1.0 strong</span>, 
                <span className="text-orange-600"> ±0.5-0.8 moderate</span>, 
                <span className="text-gray-600"> &lt;0.5 weak</span>
              </div>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={moodData.correlations} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[-1, 1]} />
                  <YAxis dataKey="pillar" type="category" width={120} />
                  <Tooltip 
                    formatter={(value) => [
                      `${(value * 100).toFixed(1)}%`,
                      "Correlation"
                    ]}
                  />
                  <Bar 
                    dataKey="correlation" 
                    name="Mood Correlation"
                    radius={[0, 4, 4, 0]}
                  >
                    {moodData.correlations.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={
                          Math.abs(entry.correlation) >= 0.8 ? "#22c55e" :
                          Math.abs(entry.correlation) >= 0.5 ? "#f59e0b" :
                          "#9ca3af"
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-2">Key Insights:</h4>
                <ul className="space-y-1 text-sm text-gray-700">
                  {moodData.correlations.slice(0, 3).map((item, idx) => (
                    <li key={idx}>
                      <strong>{item.pillar}</strong> shows {Math.abs(item.correlation) >= 0.8 ? 'strong' : Math.abs(item.correlation) >= 0.5 ? 'moderate' : 'weak'} {item.correlation >= 0 ? 'positive' : 'negative'} correlation ({(item.correlation * 100).toFixed(1)}%)
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <p className="text-center py-10 text-gray-500">
              Need at least 3 users with mood data for correlation analysis
            </p>
          )}
        </div>

        {/* Mental Sharpness Correlation Analysis */}
        <div className="bg-white p-6 rounded-lg shadow-md md:col-span-2">
          <h3 className="text-xl font-semibold mb-4">Mental Sharpness Correlations with Health Pillars</h3>
          {sharpnessData ? (
            <>
              <div className="text-center text-sm text-gray-600 mb-4">
                Based on {sharpnessData.sampleSize} users | Scale: 0-5 (5 being sharpest)
              </div>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={sharpnessData.correlations} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[-1, 1]} />
                  <YAxis dataKey="pillar" type="category" width={120} />
                  <Tooltip 
                    formatter={(value) => [
                      `${(value * 100).toFixed(1)}%`,
                      "Correlation"
                    ]}
                  />
                  <Bar 
                    dataKey="correlation" 
                    name="Sharpness Correlation"
                    radius={[0, 4, 4, 0]}
                  >
                    {sharpnessData.correlations.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={
                          Math.abs(entry.correlation) >= 0.8 ? "#22c55e" :
                          Math.abs(entry.correlation) >= 0.5 ? "#f59e0b" :
                          "#9ca3af"
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-2">Key Insights:</h4>
                <ul className="space-y-1 text-sm text-gray-700">
                  {sharpnessData.correlations.slice(0, 3).map((item, idx) => (
                    <li key={idx}>
                      <strong>{item.pillar}</strong> shows {Math.abs(item.correlation) >= 0.8 ? 'strong' : Math.abs(item.correlation) >= 0.5 ? 'moderate' : 'weak'} {item.correlation >= 0 ? 'positive' : 'negative'} correlation ({(item.correlation * 100).toFixed(1)}%)
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <p className="text-center py-10 text-gray-500">
              Need at least 3 users with mental sharpness data for correlation analysis
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderUserDetail = () => {
    if (!selectedUser) return null;

    // Match the display names to the actual data structure
    const userData = [
      { name: "Diet", value: selectedUser.diet },
      { name: "Exercise", value: selectedUser.exercise },
      { name: "Sleep", value: selectedUser.sleep },
      { name: "Community", value: selectedUser.community },
      { name: "Mental Activity", value: selectedUser.mentalActivity },
      { name: "Stress Management", value: selectedUser.stress },
      { name: "General Health", value: selectedUser.generalHealth },
      { name: "Mental Health", value: selectedUser.mentalHealth },
      { name: "Environmental Factors", value: selectedUser.environment },
    ];
    
    const userMood = selectedUser.rawAnswers?.mental_health_overallmood;
    const userSharpness = selectedUser.rawAnswers?.mental_sharpness;

    return (
      <div>
        <button
          onClick={() => setActiveTab("users")}
          className="mb-4 flex items-center text-green-700 hover:text-green-900"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Back to User List
        </button>

        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold">{selectedUser.name}</h2>
              <p className="text-gray-600">Email: {selectedUser.email}</p>
              <p className="text-gray-600">
                Completed: {selectedUser.completedDate}
              </p>
              {userMood !== undefined && (
                <p className="text-gray-600">
                  Mood Score: <span className="font-semibold">{userMood}/10</span>
                </p>
              )}
              {userSharpness !== undefined && (
                <p className="text-gray-600">
                  Mental Sharpness: <span className="font-semibold">{userSharpness}/5</span>
                </p>
              )}
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-700 mb-1">
                {selectedUser.overall.toFixed(1)}
              </div>
              <p className="text-sm text-gray-600">Overall Score</p>
            </div>
          </div>

          <h3 className="text-xl font-semibold mb-4">Pillar Scores</h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={userData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" domain={[0, 10]} />
              <YAxis dataKey="name" type="category" width={150} />
              <Tooltip />
              <Bar
                dataKey="value"
                name="Score"
                background={{ fill: "#eee" }}
                radius={[0, 4, 4, 0]}
              >
                {userData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      entry.value >= 8
                        ? "#4ade80"
                        : entry.value >= 6
                        ? "#86efac"
                        : entry.value >= 4
                        ? "#fcd34d"
                        : "#f87171"
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-xl font-semibold mb-4">
            Suggested Interventions
          </h3>
          <div className="space-y-4">
            {userData
              .filter((item) => item.value < 6)
              .sort((a, b) => a.value - b.value)
              .map((item) => (
                <div
                  key={item.name}
                  className="border-l-4 border-yellow-500 pl-4 py-2"
                >
                  <h4 className="font-medium">{item.name} Enhancement Plan</h4>
                  <p className="text-gray-600">
                    Current score:{" "}
                    <span className="font-semibold">
                      {item.value.toFixed(1)}/10
                    </span>
                  </p>
                  <p className="mt-1">
                    {item.name === "Diet" &&
                      "Recommend Mediterranean diet consultation with nutritionist."}
                    {item.name === "Exercise" &&
                      "Schedule fitness assessment and personalized exercise plan."}
                    {item.name === "Sleep" &&
                      "Suggest sleep tracking and sleep hygiene coaching sessions."}
                    {item.name === "Community" &&
                      "Recommend social engagement activities and community groups."}
                    {item.name === "Mental Activity" &&
                      "Suggest brain training games and cognitive challenges."}
                    {item.name === "Stress Management" &&
                      "Schedule stress management sessions and mindfulness training."}
                    {item.name === "General Health" &&
                      "Recommend comprehensive health check-up and follow-up plan."}
                    {item.name === "Mental Health" &&
                      "Suggest mental health screening and appropriate referrals."}
                    {item.name === "Environmental Factors" &&
                      "Provide guidance on reducing exposure to environmental toxins."}
                  </p>
                </div>
              ))}

            {userData.filter((item) => item.value < 6).length === 0 && (
              <p className="text-green-700">
                All pillar scores are in good range. Recommend maintenance
                program to sustain current practices.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderUserList = () => (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold">User Test Results</h3>
        <button
          onClick={exportToCSV}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
          Export to CSV
        </button>
      </div>

      {loading ? (
        <p className="text-center py-4">Loading user data...</p>
      ) : error ? (
        <p className="text-center py-4 text-red-500">{error}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead>
              <tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal">
                <th className="py-3 px-6 text-left">Name</th>
                <th className="py-3 px-6 text-left">Email</th>
                <th className="py-3 px-6 text-center">Date</th>
                <th className="py-3 px-6 text-center">Overall Score</th>
                <th className="py-3 px-6 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="text-gray-600 text-sm">
              {userData.map((user) => (
                <tr
                  key={user.id}
                  className="border-b border-gray-200 hover:bg-gray-50"
                >
                  <td className="py-3 px-6 text-left whitespace-nowrap">
                    {user.name}
                  </td>
                  <td className="py-3 px-6 text-left">{user.email}</td>
                  <td className="py-3 px-6 text-center">
                    {user.completedDate}
                  </td>
                  <td className="py-3 px-6 text-center">
                    <span
                      className={`py-1 px-3 rounded-full text-xs ${
                        user.overall >= 8
                          ? "bg-green-200 text-green-800"
                          : user.overall >= 6
                          ? "bg-blue-200 text-blue-800"
                          : user.overall >= 4
                          ? "bg-yellow-200 text-yellow-800"
                          : "bg-red-200 text-red-800"
                      }`}
                    >
                      {user.overall.toFixed(1)}
                    </span>
                  </td>
                  <td className="py-3 px-6 text-center">
                    <button
                      onClick={() => handleUserSelect(user)}
                      className="text-green-600 hover:text-green-900"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <h1 className="text-3xl font-bold text-gray-900">
              NeuroAge Admin Dashboard
            </h1>
            <div className="flex items-center space-x-4">
              <span className="text-gray-600">Admin User</span>
              <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center text-white font-medium">
                AU
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <nav className="flex space-x-4">
            <button
              className={`px-3 py-2 rounded-md ${
                activeTab === "overview"
                  ? "bg-green-100 text-green-800"
                  : "text-gray-600 hover:text-gray-800"
              }`}
              onClick={() => setActiveTab("overview")}
            >
              Overview
            </button>
            <button
              className={`px-3 py-2 rounded-md ${
                activeTab === "users"
                  ? "bg-green-100 text-green-800"
                  : "text-gray-600 hover:text-gray-800"
              }`}
              onClick={() => setActiveTab("users")}
            >
              User Results
            </button>
            <button
              className={`px-3 py-2 rounded-md ${
                activeTab === "reports"
                  ? "bg-green-100 text-green-800"
                  : "text-gray-600 hover:text-gray-800"
              }`}
              onClick={() => setActiveTab("reports")}
            >
              Reports
            </button>
            <button
              className={`px-3 py-2 rounded-md ${
                activeTab === "settings"
                  ? "bg-green-100 text-green-800"
                  : "text-gray-600 hover:text-gray-800"
              }`}
              onClick={() => setActiveTab("settings")}
            >
              Settings
            </button>
          </nav>
        </div>

        {activeTab === "overview" && renderOverview()}
        {activeTab === "users" && renderUserList()}
        {activeTab === "userDetail" && renderUserDetail()}
        {activeTab === "reports" && (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4">Report Generation</h2>
            <p className="text-gray-600 mb-4">
              Generate comprehensive reports based on user data.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border rounded-md p-4">
                <h3 className="font-medium mb-2">Full Data Export</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Export all quiz data with individual answers and scores in CSV format
                </p>
                <button 
                  onClick={exportToCSV}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Download CSV
                </button>
              </div>
              <div className="border rounded-md p-4">
                <h3 className="font-medium mb-2">Custom Report</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Build a report with specific parameters
                </p>
                <button className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">
                  Create Custom
                </button>
              </div>
            </div>
          </div>
        )}
        {activeTab === "settings" && (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4">Dashboard Settings</h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-medium mb-2">Quiz Configuration</h3>
                <div className="p-4 border rounded-md">
                  <div className="flex justify-between items-center mb-4">
                    <span>Scoring Algorithm Version</span>
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
                      v2.1
                    </span>
                  </div>
                  <button className="px-4 py-2 border border-green-600 text-green-600 rounded-md hover:bg-green-50">
                    Modify Quiz Parameters
                  </button>
                </div>
              </div>

              <div>
                <h3 className="font-medium mb-2">Data Management</h3>
                <div className="p-4 border rounded-md">
                  <p className="text-sm text-gray-600 mb-4">
                    Export all quiz data including individual answers and scores
                  </p>
                  <button 
                    onClick={exportToCSV}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Export All Data to CSV
                  </button>
                </div>
              </div>

              <div>
                <h3 className="font-medium mb-2">Access URL</h3>
                <div className="p-4 border rounded-md">
                  <p className="text-sm text-gray-600 mb-2">
                    Share this URL with authorized administrators only:
                  </p>
                  <code className="block bg-gray-100 p-2 rounded text-xs break-all">
                    {window.location.origin}{window.location.pathname}?auth={AUTH_KEY}
                  </code>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default NeuroAgeAdminDashboard;